import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import { Plus, Edit, Trash2, UserCheck, UserPlus } from "lucide-react";
import {
  fetchTeachers,
  createTeacher,
  updateTeacher,
  deleteTeacher,
  setAssignUser,
  clearAssignUser,
} from "./redux/teacherSlice";
import { fetchClasses } from "../classes/redux/classSlice";
import { fetchSubjects, handleJoyRideRunning } from "../subjects/redux/subjectSlice";
import userStore from "../../../zustand/userStore";
import MainPage from "./MainPage";
import UserDetails from "./UserDetails";
import Joyride, { STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { teacherSteps } from "../../../tour/timetableTourSteps";
import { createPortal } from "react-dom";
import { setFormValue, setStepIndex, setStepValid, startTour, stopTour } from "../../../redux/globalJoyrideSlice";
import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";
import { fetchOrgUsersById } from "../../../pages/user/redux/userThunk";
import "./TeacherManagement.css";

const TeacherManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const nameRef = useRef(null);
  const qualificationRef = useRef(null);

  const { teachers, loading, error, assignUser } = useSelector(
    (state) => state.teachers
  );
  const { classes } = useSelector((state) => state.classes);
  const { subjects } = useSelector((state) => state.subjects);
  const { users } = useSelector((state) => state.user);
  const masterData = useSelector((state) => state.masterData);
  const userData = userStore((state) => state.user);

  const schoolId = masterData?.school_id;
  const userId = userData?.id;
  const adminId = userData?.admin_id;
  const [runTour, setRunTour] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    qualification: "",
    max_periods_per_day: 6,
  });
  const [subjectAssignments, setSubjectAssignments] = useState({});
  const [formError, setFormError] = useState("");
  const [openForTour, setOpenForTour] = useState(false);
  const [showUserDetailsPopup, setShowUserDetailsPopup] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const location = useLocation();

  const { activeTour } = useSelector((state) => state.globalJoyride);
  const [activeSubjectId, setActiveSubjectId] = useState(null);

  const handleAssignUserClick = (teacher) => {
    const userNumberLimit = userData?.payments?.[0]?.no_of_users ?? 0;
    const planType = userData?.payments?.[0]?.plan_type ?? "";
    const isRestrictedRole = ["guest_user", "teacher"].includes(userData?.roles);

    // Calculate created users excluding logged-in user
    const createdUsersCount = (users || []).filter((u) => u.id !== userId).length;

    if (isRestrictedRole && createdUsersCount >= userNumberLimit - 1) {
      return alert("Your limit exceeded, please upgrade your plan.");
    }

    if (planType === "customized" && createdUsersCount >= userNumberLimit) {
      return alert(
        `You have reached the maximum number of users allowed in your plan.`
      );
    }

    dispatch(setAssignUser(teacher));
  };

  useEffect(() => {
    if (schoolId && userId) {
      dispatch(fetchTeachers(schoolId));
      dispatch(fetchClasses(schoolId));
      dispatch(fetchSubjects(schoolId));
      dispatch(fetchOrgUsersById(userId));
    }
  }, [dispatch, schoolId, userId]);


  useEffect(() => {
    if (showModal && openForTour) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dispatch(setStepIndex(1));
          setOpenForTour(false);
        });
      });
    }
  }, [showModal, openForTour]);
  /** SCROLL (resize fix) */


  const initializeForm = () => {
    setFormData({
      name: "",
      qualification: "",
      max_periods_per_day: 6,
    });
    setSubjectAssignments({});
    setEditingTeacher(null);
    setFormError("");
  };

  useEffect(() => {
    if (!schoolId) return;
    if (!teachers?.length) return;

    // only start if navigation explicitly requested it
    if (location.state?.startTour) {
      dispatch(startTour({
        name: "teacher",
        steps: teacherSteps({
          dispatch,
          setShowModal,
          initializeForm,
          navigate,
          stopTour: () => dispatch(stopTour()),
        }),
      }));
    }
  }, [schoolId, teachers, location.state, activeTour]);


  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      qualification: teacher.qualification,
      max_periods_per_day: teacher.max_periods_per_day,
    });

    const assignments = {};
    teacher.assignments?.forEach((assignment) => {
      if (!assignments[assignment.subject_id]) {
        assignments[assignment.subject_id] = [];
      }
      assignments[assignment.subject_id].push(assignment.class_id);
    });

    setSubjectAssignments(assignments);
    setShowModal(true);

    // 👉 Move tour to "name input" step (global joyride)
    setTimeout(() => {
      dispatch(setStepIndex(1));   // ✅ global engine
    }, 200);
  };


  const handleSubjectSelect = (subjectId) => {
    setActiveSubjectId(subjectId);
    setSubjectAssignments((prev) => {
      const newAssignments = { ...prev };
      if (newAssignments[subjectId]) {
        delete newAssignments[subjectId];
      } else {
        newAssignments[subjectId] = [];
      }
      return newAssignments;
    });

    // 👉 Move tour to "class selection" step
    setTimeout(() => {
      // let DOM render class list
      window.dispatchEvent(new Event("resize")); // recalc spotlight
      dispatch(setStepIndex(4)); // ✅ global joyride step index
    }, 300);
  };


  const handleClassSelect = (subjectId, classId, isChecked) => {
    setSubjectAssignments((prev) => {
      const currentClasses = prev[subjectId] || [];
      const updatedClasses = isChecked
        ? [...currentClasses, classId]
        : currentClasses.filter((id) => id !== classId);

      return {
        ...prev,
        [subjectId]: updatedClasses,
      };
    });

    // 👉 Move tour to "Create Teacher" step when first class is selected
    if (isChecked) {
      setTimeout(() => {
        const btn = document.querySelector(".create-teacher-step");
        //if (btn) btn.scrollIntoView({ behavior: "smooth", block: "center" });

        window.dispatchEvent(new Event("resize")); // recalc spotlight
        //dispatch(setStepIndex(5)); // ✅ global joyride
      }, 300);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!schoolId || !userId) {
      setFormError(
        "School ID or User ID is missing. Cannot perform this action."
      );
      return;
    }

    // Must select at least one subject
    if (Object.keys(subjectAssignments).length === 0) {
      setFormError(
        "Please select at least one subject and link it to a class."
      );
      return;
    }

    // Check that at least one subject has at least one class
    const validAssignments = Object.entries(subjectAssignments).some(
      ([_, classIds]) => classIds && classIds.length > 0
    );

    if (!validAssignments) {
      setFormError(
        "Please assign at least one class to at least one selected subject."
      );
      return;
    }

    // Prepare assignments array for backend
    const assignments = [];
    Object.entries(subjectAssignments).forEach(([subjectId, classIds]) => {
      classIds.forEach((classId) => {
        assignments.push({
          subject_id: Number(subjectId),
          class_id: Number(classId),
        });
      });
    });

    try {
      const teacherData = {
        ...formData,
        assignments,
        school_id: schoolId,
        user_id: userId,
      };

      if (editingTeacher) {
        await dispatch(
          updateTeacher({
            id: editingTeacher.teacher_id,
            data: teacherData,
          })
        ).unwrap();
      } else {
        await dispatch(createTeacher(teacherData)).unwrap();
        //navigate("/timetable/classes", { state: { startClassTour: true } });
        setTimeout(() => {
          setStepIndex(6); // confirmation step index
        }, 300);
      }

      setShowModal(false);
      initializeForm();
      dispatch(fetchTeachers(schoolId));
    } catch (submitError) {
      setFormError(submitError.message || "Please try again");
    }
  };

 const handleDelete = async (id) => {
  if (!userId) {
    alert("User ID is missing. Cannot perform delete action.");
    return;
  }

  if (window.confirm("Are you sure you want to delete this teacher?")) {
    try {
      await dispatch(
        deleteTeacher({ id, user_id: userId })
      ).unwrap();

      dispatch(fetchTeachers(schoolId));

      alert("Teacher deleted successfully.");
    } catch (deleteError) {
      console.error("Delete teacher error:", deleteError);

      alert(
        deleteError?.error ||
        deleteError?.message ||
        "Failed to delete teacher. Please try again."
      );
    }
  }
};
  return (
    <>
      <div className="teacher-confirm-anchor" />
      {/* <GlobalJoyrideCloseButton /> */}
      <div className="px-5 teacher-management-wrapper">
        <Row className="py-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="btn btn-primary btn-sm add-teacher-step"
                onClick={() => {
                  initializeForm();
                  setShowModal(true);

                  // 👉 move tour to "Name input" step (global joyride)
                  setTimeout(() => {
                    dispatch(setStepIndex(1));   // ✅ global engine
                  }, 200);

                  setTimeout(() => {
                    if (nameRef.current) nameRef.current.focus();
                  }, 0);
                }}
                className="d-flex align-items-center"
              >
                <Plus size={20} className="me-2" />
                Add Teacher
              </Button>

            </div>
          </Col>
        </Row>

        {/* ERROR */}
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* TEACHERS LIST */}
        <Card className="shadow-sm">
          <Card.Header className="bg-light border-bottom">
            <h5 className="mb-0 d-flex align-items-center text-primary">
              <UserCheck size={24} className="me-2" />
              All Teachers
            </h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Qualification</th>
                    <th>Max Periods/Day</th>
                    <th>Subjects</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {!(teachers.length > 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No teachers found. Add a new teacher to get started!
                      </td>
                    </tr>
                  ) : (
                    teachers.map((teacher) => (
                      <tr key={teacher.teacher_id}>
                        <td>{teacher.name}</td>
                        <td>{teacher.qualification}</td>
                        <td>
                          <span className="badge bg-secondary">
                            {teacher.max_periods_per_day}
                          </span>
                        </td>
                        <td>
                          {teacher.subjects?.map((subject) => (
                            <span
                              key={subject.subject_id}
                              className="badge bg-light text-dark me-1"
                            >
                              {subject.subject_name}
                            </span>
                          ))}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1 flex-nowrap">
                          <Button
                            size="sm"
                            variant="link"
                            className="text-primary p-1"
                            onClick={() => handleEdit(teacher)}
                          >
                            <Edit size={16} />
                          </Button>

                          <Button
                            size="sm"
                            variant="link"
                            className="text-danger p-1"
                            onClick={() => handleDelete(teacher.teacher_id)}
                          >
                            <Trash2 size={16} />
                          </Button>

                         
                           {teacher.user_id === null ? (
                             <Button
                               size="sm"
                               variant="link"
                               className="text-success p-1"
                               title="Assign User"
                               onClick={() => handleAssignUserClick(teacher)}
                             >
                               <UserPlus size={16} />
                             </Button>
                           ) : (
                             <Button
                               size="sm"
                               variant="link"
                               className="text-info p-1"
                               title="View Assigned User Details"
                               onClick={() => {
                                 setSelectedUserId(teacher.user_id);
                                 setShowUserDetailsPopup(true);
                               }}
                             >
                               <UserCheck size={16} />
                             </Button>
                           )} 
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

              </Table>
            )}
          </Card.Body>
        </Card>

        {/* ASSIGN USER POPUP */}
        {assignUser && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1050,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "15px",
                width: "90%",
                maxWidth: "600px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                  borderBottom: "1px solid #dee2e6",
                  paddingBottom: "10px",
                }}
              >
                <h6 style={{ margin: 0, fontWeight: "600" }}>Assign User</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(clearAssignUser())}
                  style={{ border: "none", background: "none", fontSize: "20px", cursor: "pointer" }}
                >
                  &times;
                </button>
              </div>
              <MainPage />
            </div>
          </div>
        )}

        {showUserDetailsPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1050,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "15px",
                width: "90%",
                maxWidth: "500px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                  borderBottom: "1px solid #dee2e6",
                  paddingBottom: "10px",
                }}
              >
                <h6 style={{ margin: 0, fontWeight: "600" }}>Assigned User Details</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUserDetailsPopup(false);
                    setSelectedUserId(null);
                  }}
                  style={{ border: "none", background: "none", fontSize: "20px", cursor: "pointer" }}
                >
                  &times;
                </button>
              </div>
              <UserDetails
                userId={selectedUserId}
                onClose={() => {
                  setShowUserDetailsPopup(false);
                  setSelectedUserId(null);
                }}
              />
            </div>
          </div>
        )}

        {/* MODAL */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          centered
          backdrop="static"
          keyboard={false}
          className="teacher-management-modal"
        >
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                {editingTeacher ? "Edit Teacher" : "Add New Teacher"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {formError && <Alert variant="danger">{formError}</Alert>}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Teacher Name</Form.Label>
                    <Form.Control
                      type="text"
                      className="teacher-name-step"
                      ref={nameRef}
                      value={formData.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, name: val });
                        dispatch(setFormValue({       // ✅ sync
                          key: "teacherName",
                          value: val,
                        }));
                      }}
                      placeholder="Enter teacher's full name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Qualification</Form.Label>
                    <Form.Control
                      type="text"
                      className="teacher-qualification-step"
                      ref={qualificationRef}
                      value={formData.qualification}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, qualification: val });
                        dispatch(setFormValue({
                          key: "teacherQualification",
                          value: val,
                        }));
                      }}
                      placeholder="e.g., M.Ed, B.Ed, M.A"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Subjects and classes */}
              <Form.Group className="mb-3">
                <Form.Label>Max Periods Per Day</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_periods_per_day}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_periods_per_day: parseInt(e.target.value),
                    })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Select subjects and assign classes for each subject
                </Form.Label>
                {subjects.length === 0 ? (
                  <Alert variant="info">
                    No subjects available. Please add subjects first.
                  </Alert>
                ) : (
                  subjects.map((subject, i) => (
                    <Card key={subject.subject_id} className="mb-2 subject-step">
                      <Card.Body className="pb-0">
                        <Form.Check
                          type="checkbox"
                          id={`subject-${subject.subject_id}`}
                          label={<strong>{subject.subject_name}</strong>}
                          checked={!!subjectAssignments[subject.subject_id]}
                          onChange={() =>
                            handleSubjectSelect(subject.subject_id)
                          }
                        />

                        {subjectAssignments[subject.subject_id] && (
                          <div className="mt-2">
                            <Form.Label className="small">
                              Classes for {subject.subject_name}:
                            </Form.Label>
                            <div className="border rounded p-3 bg-light-subtle">
                              {classes.length === 0 ? (
                                <p className="text-muted fst-italic">
                                  No classes available
                                </p>
                              ) : (
                                <Row className="g-2">
                                  {classes.map((cls, j) => (
                                    <Col key={cls.class_id} md={6}>
                                      <Form.Check
                                        type="checkbox"
                                        id={`cls-${subject.subject_id}-${cls.class_id}`}
                                        className={`${subject.subject_id === activeSubjectId ? "class-step" : ""}`}
                                        label={cls.class_name}
                                        checked={subjectAssignments[
                                          subject.subject_id
                                        ]?.includes(Number(cls.class_id))}
                                        onChange={(e) =>
                                          handleClassSelect(
                                            subject.subject_id,
                                            Number(cls.class_id),
                                            e.target.checked,
                                            dispatch(setStepValid(true)),
                                          )
                                        }
                                      />
                                    </Col>
                                  ))}
                                </Row>
                              )}
                            </div>
                          </div>
                        )}

                      </Card.Body>
                    </Card>
                  ))
                )}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>

              <Button
                variant="primary"
                type="submit"
                className="create-teacher-step"
                disabled={loading}
              >
                {editingTeacher ? "Update Teacher" : "Create Teacher"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>

      {/* Modal scroll fix */}
      <style>
        {`
          body.modal-open {
  overflow: hidden !important;
}


          .teacher-management-modal {
  position: absolute !important;
}

.teacher-management-modal .modal-body {
  transform: translateZ(0) !important;
}
        `}
      </style>
    </>
  );
};

export default TeacherManagement;
